<template>
  <modal
    :name="name"
    :width="width"
    :height="height"
    :classes="['cp-modal', modalClass]"
    :styles="initialStyles"
    @opened="onOpened">
    <div
      class="cp-modal__header"
      :class="handleClass"
      ref="dragHandle">
      <span class="cp-modal__title">{{ title }}</span>
      <span
        class="cp-modal__close"
        @click="close"
        ><b-icon icon="close-thick" style="color: white;"></b-icon></span
      >
    </div>
    <div
      class="cp-modal__body">
      <slot />
    </div>
  </modal>
</template>

<script>
const BASE_Z_MIN = 30;
const BASE_Z_MAX = 35;
let zCounter = BASE_Z_MIN;

export default {
  name: 'FloatingModal',
  props: {
    name: { type: String, required: true },
    title: { type: String, default: '' },
    width: { type: [Number, String], default: 800 },
    height: { type: [Number, String], default: null },
    top: { type: [Number, String], default: 100 },
    left: { type: [Number, String], default: null },
    right: { type: [Number, String], default: 100 },
    handleClass: { type: String, default: 'handle' },
    modalClass: { type: String, default: '' }
  },
  data () {
    return {
      frontEl: null,
      isDragging: false,
      dragStartX: 0,
      dragStartY: 0,
      startLeft: 0,
      startTop: 0
    }
  },
  computed: {
    px () { return v => (typeof v === 'number' ? `${v}px` : v) },
    initialStyles () {
      const s = { position: 'fixed', transform: 'none', margin: '0', top: this.px(this.top) }
      if (this.left != null) {
        s.left = this.px(this.left); s.right = 'auto'
      } else {
        s.right = this.px(this.right != null ? this.right : 100); s.left = 'auto'
      }
      return s
    }
  },

  mounted () {
    window.addEventListener('mouseup', this.keepInside, { passive: true })
    window.addEventListener('resize', this.applyInitialPosition, { passive: true })
  },
  beforeDestroy () {
    window.removeEventListener('mouseup', this.keepInside)
    window.removeEventListener('resize', this.applyInitialPosition)
    this.detachBringToFront()
    this.detachDrag()
    this.onDragEnd()
  },

  methods: {
    close: function() {
      this.$emit('close')
    },
    onOpened () {
      this.$nextTick(() => {
        this.applyInitialPosition()
        this.bringToFront()
        this.attachDrag()
        this.attachBringToFront()
      })
    },
    // 操作モーダルの要素を取得
    getContainerEl () {
      if (this.modalClass) {
        const box =
          document.querySelector(`.v--modal-overlay .v--modal-box.cp-modal.${this.modalClass}`) ||
          document.querySelector(`.v--modal-overlay .v--modal-box.${this.modalClass}`) ||
          document.querySelector(`.vm--modal-overlay .vm--modal.${this.modalClass}`) ||
          document.querySelector(`.cp-modal.${this.modalClass}`)
        if (box) return box
      }
      const list = document.querySelectorAll('.v--modal-box, .vm--modal, .cp-modal')
      return list.length ? list[list.length - 1] : null
    },
    getOverlayEl () {
      const box = this.getContainerEl()
      return box ? box.closest('.v--modal-overlay, .vm--modal-overlay') : null
    },

    // 対象のモーダルを最前面へ移動する（zIndexが大きくする）
    bringToFront () {
      const box = this.getContainerEl()
      const ov = this.getOverlayEl()
      if (!box || !ov) return
      zCounter++;
      if(zCounter > BASE_Z_MAX){
        zCounter = BASE_Z_MAX;
      }
      document.querySelectorAll('.v--modal-box.cp-modal, .vm--modal').forEach(el => {
        if (el !== box) {
          el.style.zIndex = String(BASE_Z_MIN);
          const otherOv = el.closest('.v--modal-overlay, .vm--modal-overlay');
          if (otherOv) otherOv.style.zIndex = String(BASE_Z_MIN);
        }
      });
      ov.style.zIndex = String(BASE_Z_MAX);
      box.style.zIndex = String(BASE_Z_MAX);
      zCounter = BASE_Z_MAX; 
    },
    // モーダルをクリック・フォーカス時のイベント追加
    attachBringToFront () {
      const box = this.getContainerEl()
      if (!box) return
      this.frontEl = box
      box.addEventListener('pointerdown', this.bringToFront, { capture: true })
      box.addEventListener('focusin', this.bringToFront, { capture: true })
    },
    // モーダルをクリック・フォーカス時のイベント削除
    detachBringToFront () {
      const t = this.frontEl
      if (!t) return
      t.removeEventListener('pointerdown', this.bringToFront, true)
      t.removeEventListener('focusin', this.bringToFront, true)
      this.frontEl = null
    },

    // 初期位置を適用
    applyInitialPosition () {
      const el = this.getContainerEl()
      if (!el) return
      el.style.transform = 'none'
      el.style.top = this.px(this.top)
      if (this.left != null) {
        el.style.left = this.px(this.left)
        el.style.right = 'auto'
      } else {
        el.style.right = this.px(this.right != null ? this.right : 100)
        el.style.left = 'auto'
      }
    },
    // 画面外にはみ出さないように補正
    keepInside () {
      const el = this.getContainerEl()
      if (!el) return
      const r = el.getBoundingClientRect()
      const top = Math.min(Math.max(r.top, 0), Math.max(0, window.innerHeight - r.height))
      const left = Math.min(Math.max(r.left, 0), Math.max(0, window.innerWidth - el.offsetWidth))
      el.style.top = `${Math.round(top)}px`
      el.style.left = `${Math.round(left)}px`
      el.style.right = 'auto'
    },

    // ドラッグ開始イベント追加
    attachDrag () {
      const handle = this.$refs.dragHandle
      if (!handle) return
      handle.addEventListener('pointerdown', this.onDragStart, { passive: false })
    },
    // ドラッグ開始イベント削除
    detachDrag () {
      const handle = this.$refs.dragHandle
      if (!handle) return
      handle.removeEventListener('pointerdown', this.onDragStart)
    },
    // ドラッグ開始（ドラッグ中・ドラッグ終了イベント追加）
    onDragStart (e) {
      const el = this.getContainerEl()
      if (!el) return
      this.bringToFront()
      const r = el.getBoundingClientRect()
      this.isDragging = true
      this.dragStartX = e.clientX
      this.dragStartY = e.clientY
      this.startLeft = r.left
      this.startTop = r.top
      document.addEventListener('pointermove', this.onDragMove, { passive: false })
      document.addEventListener('pointerup', this.onDragEnd, { passive: true })
      document.body.style.userSelect = 'none'
      e.preventDefault()
    },
    // ドラッグ中
    onDragMove (e) {
      if (!this.isDragging) return
      const el = this.getContainerEl()
      if (!el) return
      const dx = e.clientX - this.dragStartX
      const dy = e.clientY - this.dragStartY
      let left = this.startLeft + dx
      let top = this.startTop + dy
      left = Math.min(Math.max(left, 0), Math.max(0, window.innerWidth - el.offsetWidth))
      top = Math.min(Math.max(top, 0), Math.max(0, window.innerHeight - el.offsetHeight))
      el.style.left = `${Math.round(left)}px`
      el.style.top = `${Math.round(top)}px`
      el.style.right = 'auto'
      e.preventDefault()
    },
    // ドラッグ終了（ドラッグ中・ドラッグ終了イベント削除）
    onDragEnd () {
      if (!this.isDragging) return
      this.isDragging = false
      document.removeEventListener('pointermove', this.onDragMove)
      document.removeEventListener('pointerup', this.onDragEnd)
      document.body.style.userSelect = ''
    }
  }
}
</script>

<style lang="scss">
  .v--modal-overlay,
  .vm--modal-overlay {
    background: transparent !important;
    pointer-events: none !important;
    position: fixed !important;
    display: block !important;
    align-items: initial !important;
    justify-content: initial !important;
  }

  .v--modal-overlay .v--modal-box,
  .vm--modal-overlay .vm--modal,
  .vm--modal-overlay .cp-modal {
    pointer-events: auto !important;
  }

  .v--modal-box.cp-modal {
    position: fixed !important;
    transform: none !important;
    margin: 0 !important;
  }

  .cp-modal {
    display: flex;
    flex-direction: column;
    border-radius: 2px;
    border: 1px solid #000;
    background: #fff;
    box-shadow: 4px 4px 8px rgba(0, 0, 0, 0.8);
    border: 1px solid #3006f0;
  }

  .cp-modal__header {
    height: 40px;
    padding: 8px 12px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    background: rgba(0, 0, 139, 0.7);
    color: #fff;
    user-select: none;
    cursor: move;
    touch-action: none;
    -ms-touch-action: none;
  }

  .cp-modal__close {
    cursor:pointer;
    top:10px;
    right: 10px;
  }

  .cp-modal__body {
    flex: auto;
    margin: 5px 8px 5px;
    min-height: 0;
    overflow: auto;
    overscroll-behavior: contain;
    background: #fff;
    -webkit-overflow-scrolling: touch;
  }
</style>



// WEBPACK FOOTER //
// src/components/mock/common/FloatingModal.vue